import React from "react";
import { Sticky } from "react-sticky";
import Icon from "@mdi/react";
import { mdiMenu, mdiMenuDown, mdiMenuUp } from "@mdi/js";
import { PICK_TYPES } from "@rotoql/common-services";

import "../styles.scss";
import {
  WEB_SPORTS_MAP,
  COLUMN_TEAMS,
  // COLUMN_DATE,
  COLUMN_CURRENT,
  COLUMN_OPEN,
  COLUMN_VALUE_RATING,
  COLUMN_PUBLIC_TICKET_PCT,
  COLUMN_PUBLIC_MONEY,
  COLUMN_LINE_MOVE_SPREAD,
  COLUMN_INJURIES,
  COLUMN_PRO_MONEY_EDGE,
  COLUMN_TOTAL_GAMES,
  COLUMN_LINE_MOVE_ML,
  COLUMN_LINE_MOVE_OU,
  COLUMN_UNDER_RECORD,
  COLUMN_SEASON_WIN_PCT,
  COLUMN_ATS_RECORD,
  COLUMN_ATS_WIN_PCT,
  COLUMN_OVER_RECORD,
  COLUMN_OVER_PCT,
  COLUMN_UNDER_PCT,
  COLUMN_ROT_NUMBER,
  COLUMN_CURRENT_SPREAD,
  COLUMN_OPEN_SPREAD,
  COLUMN_CURRENT_ML,
  COLUMN_OPEN_ML,
  COLUMN_CURRENT_OU,
  COLUMN_OPEN_OU,
  COLUMN_PUBLIC_TICKET_TOT_SPREAD,
  COLUMN_PUBLIC_TICKET_TOT_ML,
  COLUMN_PUBLIC_TICKET_TOT_OVER,
  COLUMN_PUBLIC_TICKET_TOT_UNDER,
  COLUMN_NET_UNITS,
  COLUMN_ATS_UNITS,
  COLUMN_PITCHERS,
  COLUMN_PTS_FOR,
  COLUMN_PTS_AGAINST,
  COLUMN_HOME_RECORD,
  COLUMN_ROAD_RECORD,
  COLUMN_HOME_ATS_RECORD,
  COLUMN_ROAD_ATS_RECORD,
  COLUMN_HOME_OU_RECORD,
  COLUMN_ROAD_OU_RECORD,
  COLUMN_LINE_MOVE,
  COLUMN_BETQL_MODEL,
  COLUMN_FADE_THE_PUBLIC,
  COLUMN_SHARP_BETTING,
  COLUMN_BETQL_COMMUNITY,
  COLUMN_CONSENSUS,
} from "../../../constants";

const SharpIcon = require("public/icon/sharp-icon.png?sizes[]=20,sizes[]=40,sizes[]=60");

interface GetHeaderJsxArgs {
  /** (optional) Provide a custom column className for this header */
  classString?: string;
  /** The title text for the column */
  title: string;
  /** The ID of this column */
  columnId: number;
  /** The ID of the column we want to sort by */
  sortColumnId: number;
  /** A function to set the column we are sorting by */
  onSelectSortColumn: (columnId: number) => void;
  columnSortDesc: boolean;
  isSharp?: boolean;
  isNotSortable?: boolean;
  isNotDraggable?: boolean;
}

/** This function generates the column header JSX to properly show sorting and titles */
const getHeaderJSX = ({
  classString,
  title,
  columnId,
  sortColumnId,
  onSelectSortColumn,
  columnSortDesc,
  isSharp,
  isNotSortable,
  isNotDraggable,
}: GetHeaderJsxArgs) => {
  const className: string = classString || "games-table-header__column";
  return [
    <Sticky key="header">
      {({ style, isSticky }: any) => (
        <div
          style={{
            transform: "translateZ(0px)",
            ...style,
          }}
          className={`${className} ${isSticky ? "games-table-header__column-sticky" : ""}`}
          onClick={() => (isNotSortable ? {} : onSelectSortColumn(columnId))}
        >
          {!isNotDraggable ? <Icon path={mdiMenu} size="16px" color="#888888" /> : null}
          {title}
          <div className="games-table-header__column-sort-icon">
            {!isNotSortable && sortColumnId === columnId ? (
              columnSortDesc ? (
                <Icon path={mdiMenuDown} size="25px" color="#888888" />
              ) : (
                <Icon path={mdiMenuUp} size="25px" color="#888888" />
              )
            ) : null}
          </div>
          {isSharp ? (
            <img
              data-src={SharpIcon.src}
              data-srcset={SharpIcon.srcSet}
              alt="Sharp Tier Icon"
              className="games-table-header__sharp-icon lazyload"
            />
          ) : null}
        </div>
      )}
    </Sticky>,
  ];
};

/**
 * This function creates the double array structure of our table and sets
 * the headers for each column.
 *
 * NOTE: We are setting the initial transform: translateZ(0px) so that it will
 * remove the headers from the dom flow on render. This makes their z-index higher
 * than the row overlay so that we can grab the column and move it around. The
 * Sticky element will overrite this once we scroll and the header becomes sticky
 *
 * @param lineType the specific line type we are currently filtering by
 */
export const getHeaders = (
  lineType: string,
  sortColumnId: number,
  onSelectSortColumn: (columnId: number) => void,
  columnSortDesc: boolean,
  sportKey: string,
  pageKey: string
) => {
  const showLabel = lineType === "spread" && WEB_SPORTS_MAP[sportKey].favoriteLine === "spread";
  const lineTypeAbbrev: string = PICK_TYPES[lineType].abbrev;
  const fullSpAbbrev = lineTypeAbbrev.toLowerCase() === "sp" ? "Spread" : lineTypeAbbrev;
  const lineTypeLabel = showLabel ? "" : ` - ${lineTypeAbbrev}`;
  const headers: any = [];
  const isTrendBet = pageKey === "trend";
  // 0
  headers[COLUMN_TEAMS] = getHeaderJSX({
    classString: "games-table-header__team-column",
    title: "Matchup",
    columnId: COLUMN_TEAMS,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isNotDraggable: true,
    isNotSortable: isTrendBet ? true : false,
  });
  // 1
  // headers[COLUMN_DATE] = getHeaderJSX(
  //   "games-table-header__date-column",
  //   "Date",
  //   COLUMN_DATE,
  //   sortColumnId,
  //   onSelectSortColumn,
  //   columnSortDesc,
  //   false
  // );
  // 2
  headers[COLUMN_CURRENT] = getHeaderJSX({
    classString: "games-table-header__current-line-column",
    title: `Live - ${fullSpAbbrev}`,
    columnId: COLUMN_CURRENT,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isNotSortable: isTrendBet ? true : false,
  });

  // 3
  headers[COLUMN_OPEN] = getHeaderJSX({
    title: `Open - ${fullSpAbbrev}`,
    columnId: COLUMN_OPEN,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 4
  headers[COLUMN_VALUE_RATING] = getHeaderJSX({
    classString: "games-table-header__value-rating-column",
    title: "Best Bet Rating",
    columnId: COLUMN_VALUE_RATING,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 5
  headers[COLUMN_PUBLIC_TICKET_PCT] = getHeaderJSX({
    classString: "games-table-header__ticket-percent-column",
    title: `Public Betting %${lineTypeLabel}`,
    columnId: COLUMN_PUBLIC_TICKET_PCT,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 6
  headers[COLUMN_PUBLIC_MONEY] = getHeaderJSX({
    classString: "games-table-header__money-percent-column",
    title: `Public Money %${lineTypeLabel}`,
    columnId: COLUMN_PUBLIC_MONEY,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 42
  headers[COLUMN_LINE_MOVE] = getHeaderJSX({
    classString: "games-table-header__line-move-column",
    title: `Line Move${lineTypeLabel}`,
    columnId: COLUMN_LINE_MOVE,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 7
  headers[COLUMN_LINE_MOVE_SPREAD] = getHeaderJSX({
    classString: "games-table-header__line-move-column",
    title: "Line Move - Sp",
    columnId: COLUMN_LINE_MOVE_SPREAD,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 8
  headers[COLUMN_INJURIES] = getHeaderJSX({
    classString: "games-table-header__news-injuries-column",
    title: "News & Injuries",
    columnId: COLUMN_INJURIES,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isNotSortable: true,
  });
  // 9
  headers[COLUMN_PRO_MONEY_EDGE] = getHeaderJSX({
    classString: "games-table-header__pro-edge-column",
    title: `Pro Betting %${lineTypeLabel}`,
    columnId: COLUMN_PRO_MONEY_EDGE,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 10
  headers[COLUMN_TOTAL_GAMES] = getHeaderJSX({
    title: "Games",
    columnId: COLUMN_TOTAL_GAMES,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 11
  headers[COLUMN_LINE_MOVE_ML] = getHeaderJSX({
    classString: "games-table-header__line-move-column",
    title: "Line Move - ML",
    columnId: COLUMN_LINE_MOVE_ML,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 12
  headers[COLUMN_LINE_MOVE_OU] = getHeaderJSX({
    classString: "games-table-header__line-move-column",
    title: "Line Move - O/U",
    columnId: COLUMN_LINE_MOVE_OU,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 13
  headers[COLUMN_UNDER_RECORD] = getHeaderJSX({
    classString: "games-table-header__under-record-column",
    title: "Under Record",
    columnId: COLUMN_UNDER_RECORD,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 14
  headers[COLUMN_SEASON_WIN_PCT] = getHeaderJSX({
    classString: "games-table-header__season-win-column",
    title: "Season Win %",
    columnId: COLUMN_SEASON_WIN_PCT,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 15
  headers[COLUMN_ATS_RECORD] = getHeaderJSX({
    title: "ATS Rec",
    columnId: COLUMN_ATS_RECORD,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 16
  headers[COLUMN_ATS_WIN_PCT] = getHeaderJSX({
    title: "ATS Win %",
    columnId: COLUMN_ATS_WIN_PCT,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 17
  headers[COLUMN_OVER_RECORD] = getHeaderJSX({
    title: "Over Record",
    columnId: COLUMN_OVER_RECORD,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 18
  headers[COLUMN_OVER_PCT] = getHeaderJSX({
    title: "Over %",
    columnId: COLUMN_OVER_RECORD,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 19
  headers[COLUMN_UNDER_PCT] = getHeaderJSX({
    title: "Under %",
    columnId: COLUMN_UNDER_PCT,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 20
  headers[COLUMN_ROT_NUMBER] = getHeaderJSX({
    title: "Rot",
    columnId: COLUMN_ROT_NUMBER,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 21
  headers[COLUMN_CURRENT_SPREAD] = getHeaderJSX({
    title: "Live - Spread",
    columnId: COLUMN_CURRENT_SPREAD,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 22
  headers[COLUMN_OPEN_SPREAD] = getHeaderJSX({
    title: "Open - Spread",
    columnId: COLUMN_OPEN_SPREAD,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 23
  headers[COLUMN_CURRENT_ML] = getHeaderJSX({
    classString: "games-table-header__current-ml-column",
    title: "Live - ML",
    columnId: COLUMN_CURRENT_ML,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 24
  headers[COLUMN_OPEN_ML] = getHeaderJSX({
    title: "Open - ML",
    columnId: COLUMN_OPEN_ML,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 25
  headers[COLUMN_CURRENT_OU] = getHeaderJSX({
    classString: "games-table-header__current-ou-column",
    title: "Live - O/U",
    columnId: COLUMN_CURRENT_OU,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 26
  headers[COLUMN_OPEN_OU] = getHeaderJSX({
    title: "Open - O/U",
    columnId: COLUMN_OPEN_OU,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 27
  headers[COLUMN_PUBLIC_TICKET_TOT_SPREAD] = getHeaderJSX({
    classString: "games-table-header__ticket-count-spread-column",
    title: "# of Tickets - Sp",
    columnId: COLUMN_PUBLIC_TICKET_TOT_SPREAD,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 28
  headers[COLUMN_PUBLIC_TICKET_TOT_ML] = getHeaderJSX({
    classString: "games-table-header__ticket-count-ml-column",
    title: "# of Tickets - ML",
    columnId: COLUMN_PUBLIC_TICKET_TOT_ML,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 29
  headers[COLUMN_PUBLIC_TICKET_TOT_OVER] = getHeaderJSX({
    classString: "games-table-header__ticket-count-over-column",
    title: "# of Tickets - O",
    columnId: COLUMN_PUBLIC_TICKET_TOT_OVER,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 30
  headers[COLUMN_PUBLIC_TICKET_TOT_UNDER] = getHeaderJSX({
    classString: "games-table-header__ticket-count-under-column",
    title: "# of Tickets - U",
    columnId: COLUMN_PUBLIC_TICKET_TOT_UNDER,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 31
  headers[COLUMN_NET_UNITS] = getHeaderJSX({
    title: "Net Units",
    columnId: COLUMN_NET_UNITS,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 32
  headers[COLUMN_ATS_UNITS] = getHeaderJSX({
    title: "ATS Units",
    columnId: COLUMN_ATS_UNITS,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 33
  headers[COLUMN_PITCHERS] = getHeaderJSX({
    classString: "games-table-header__pitchers-column",
    title: "Pitchers",
    columnId: COLUMN_PITCHERS,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isNotSortable: true,
  });
  // 34
  headers[COLUMN_PTS_FOR] = getHeaderJSX({
    title: "Points For",
    columnId: COLUMN_PTS_FOR,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 35
  headers[COLUMN_PTS_AGAINST] = getHeaderJSX({
    classString: "games-table-header__pts-against-column",
    title: "Points Against",
    columnId: COLUMN_PTS_AGAINST,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 36
  headers[COLUMN_HOME_RECORD] = getHeaderJSX({
    title: "Home",
    columnId: COLUMN_HOME_RECORD,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 37
  headers[COLUMN_ROAD_RECORD] = getHeaderJSX({
    title: "Road",
    columnId: COLUMN_ROAD_RECORD,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
  });
  // 38
  headers[COLUMN_HOME_ATS_RECORD] = getHeaderJSX({
    title: "Home ATS",
    columnId: COLUMN_HOME_ATS_RECORD,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 39
  headers[COLUMN_ROAD_ATS_RECORD] = getHeaderJSX({
    title: "Road ATS",
    columnId: COLUMN_ROAD_ATS_RECORD,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 40
  headers[COLUMN_HOME_OU_RECORD] = getHeaderJSX({
    title: "Home O/U",
    columnId: COLUMN_HOME_OU_RECORD,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 41
  headers[COLUMN_ROAD_OU_RECORD] = getHeaderJSX({
    title: "Road O/U",
    columnId: COLUMN_ROAD_OU_RECORD,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isSharp: false,
  });
  // 43
  headers[COLUMN_BETQL_MODEL] = getHeaderJSX({
    title: "BetQL Model",
    columnId: COLUMN_BETQL_MODEL,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isNotSortable: true,
  });
  // 44
  headers[COLUMN_FADE_THE_PUBLIC] = getHeaderJSX({
    title: "Fade the Public",
    columnId: COLUMN_FADE_THE_PUBLIC,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isNotSortable: true,
  });
  // 45
  headers[COLUMN_SHARP_BETTING] = getHeaderJSX({
    title: "Sharp Betting",
    columnId: COLUMN_SHARP_BETTING,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isNotSortable: true,
  });
  // 46
  headers[COLUMN_BETQL_COMMUNITY] = getHeaderJSX({
    title: "BetQL Community",
    columnId: COLUMN_BETQL_COMMUNITY,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isNotSortable: true,
  });
  // 47
  headers[COLUMN_CONSENSUS] = getHeaderJSX({
    title: "Consensus",
    columnId: COLUMN_CONSENSUS,
    sortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    isNotSortable: true,
  });

  return headers;
};
